import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message } from 'antd';
import { useSignUp, useAuth, useUser } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { THEME_COLORS, GRADIENTS, COLORS, SHADOWS } from '../../../utils/colors';
import './SignUpStep.mobile.css';

const { Title, Text } = Typography;

const INPUT_BG = 'rgba(255,255,255,0.04)';
const INPUT_BORDER = 'rgba(255,255,255,0.08)';
const INPUT_FOCUS_GLOW = '0 0 0 2px rgba(59, 130, 246, 0.25)';

const SignUpStep = ({ onNext, onBack, isMobile }) => {
  const { signUp, setActive, isLoaded } = useSignUp();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingPhone, setPendingPhone] = useState(null);
  const passwordValue = Form.useWatch('password', form);
  const passwordValid = Boolean(passwordValue && passwordValue.length >= 8 && /\d/.test(passwordValue));

  // Redirect if already signed in
  useEffect(() => {
    if (isSignedIn && isLoaded) {
      onNext();
    }
  }, [isSignedIn, isLoaded, onNext]);

  // After sign-up is complete and session is active, save phone to Clerk and advance
  useEffect(() => {
    if (!user || pendingPhone === null) return;
    let cancelled = false;
    (async () => {
      try {
        await user.update({
          unsafeMetadata: {
            ...(user.unsafeMetadata || {}),
            phoneNumber: pendingPhone.trim() || undefined,
          },
        });
        if (!cancelled) {
          setPendingPhone(null);
          onNext();
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to save phone to Clerk:', err);
          setPendingPhone(null);
          onNext(); // still advance; phone can be added later
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user, pendingPhone, onNext]);

  const handleSubmit = async (values) => {
    if (!isLoaded) return;

    setLoading(true);
    setError('');

    try {
      // Create the sign-up attempt
      const result = await signUp.create({
        emailAddress: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
      });

      // Handle different sign-up statuses
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        setPendingPhone(values.phone || '');
        setLoading(false);
        // onNext() is called from useEffect after saving phone to Clerk
      } else if (result.status === 'missing_requirements') {
        // Email verification might be required
        try {
          await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
          message.info('Please check your email to verify your account');
        } catch (verifyErr) {
          console.error('Email verification error:', verifyErr);
        }
        setLoading(false);
        onNext();
      } else {
        setLoading(false);
        onNext();
      }
    } catch (err) {
      const errorMessage = err.errors?.[0]?.message || 'An error occurred during sign up';
      setError(errorMessage);
      message.error(errorMessage);
      console.error('Sign-up error:', err);
      setLoading(false);
    }
  };

  /* Premium mobile layout - matches Dealership Details (header + glass card) */
  if (isMobile) {
    return (
      <div className="signup-mobile-wrap">
        <div className="signup-mobile-container">
          {/* Header: same as Dealership Details - Welcome to Hooptie, subtext, Step 1 of 5 • Create Your Account */}
          <header className="signup-mobile-header">
            <h1 className="signup-mobile-welcome-title">Welcome to Hooptie</h1>
            <p className="signup-mobile-header-subtext">
              AI-powered market insights and accurate pricing based on real active + sold listings in your area.
            </p>
            <div className="signup-mobile-step-text">
              <span>Step 1 of 5</span>
              <span className="bullet" />
              <span>Create Your Account</span>
            </div>
          </header>

          {/* Glass card - same style as Dealership Details */}
          <div className="signup-mobile-card">
            <div className="signup-mobile-card-header">
              <h2 className="signup-mobile-card-title">Create your account</h2>
              <p className="signup-mobile-card-subtext">Set up your Hooptie login in 30 seconds.</p>
            </div>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              {error && <div className="signup-mobile-error">{error}</div>}

              <div className="signup-mobile-name-row">
                <Form.Item
                  name="firstName"
                  rules={[{ required: true, message: 'Please enter your first name' }]}
                >
                  <div>
                    <span className="signup-mobile-label">First Name <span style={{ color: COLORS.ERROR[500] }}>*</span></span>
                    <Input placeholder="John" />
                  </div>
                </Form.Item>
                <Form.Item
                  name="lastName"
                  rules={[{ required: true, message: 'Please enter your last name' }]}
                >
                  <div>
                    <span className="signup-mobile-label">Last Name <span style={{ color: COLORS.ERROR[500] }}>*</span></span>
                    <Input placeholder="Doe" />
                  </div>
                </Form.Item>
              </div>

              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <div>
                  <span className="signup-mobile-label">Email <span style={{ color: COLORS.ERROR[500] }}>*</span></span>
                  <Input placeholder="john.doe@example.com" />
                  <p className="signup-mobile-helper">Used for onboarding + support only.</p>
                </div>
              </Form.Item>

              <Form.Item
                name="phone"
                rules={[{ required: true, message: 'Please enter your phone number' }]}
              >
                <div>
                  <span className="signup-mobile-label">Phone <span style={{ color: COLORS.ERROR[500] }}>*</span></span>
                  <Input placeholder="(555) 123-4567" />
                </div>
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: 'Please enter a password' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                  { pattern: /\d/, message: 'Password must contain at least one number' },
                ]}
              >
                <div>
                  <span className="signup-mobile-label">Password <span style={{ color: COLORS.ERROR[500] }}>*</span></span>
                  <Input.Password placeholder="••••••••" />
                  <div className="signup-mobile-password-hint">
                    {passwordValid ? <span style={{ color: '#22c55e' }}>✓</span> : <span style={{ opacity: 0.7 }}>○</span>}
                    <span>8+ characters / 1 number</span>
                  </div>
                </div>
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <div>
                  <span className="signup-mobile-label">Confirm Password <span style={{ color: COLORS.ERROR[500] }}>*</span></span>
                  <Input.Password placeholder="••••••••" />
                </div>
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={loading} disabled={!isLoaded} className="signup-mobile-btn">
                Continue
              </Button>
            </Form>
            <p className="signup-mobile-trust">Next: Dealership details • Cancel anytime</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 0, overflowX: 'hidden', width: '100%', position: 'relative' }}>
      <style>
        {`
          .signup-input:focus,
          .signup-input.ant-input-focused {
            border-color: ${COLORS.PRIMARY[500]} !important;
            box-shadow: ${INPUT_FOCUS_GLOW} !important;
            outline: none !important;
          }
          .signup-input:hover { border-color: rgba(255,255,255,0.12) !important; }
          .signup-input-password:focus,
          .signup-input-password.ant-input-focused {
            border-color: ${COLORS.PRIMARY[500]} !important;
            box-shadow: ${INPUT_FOCUS_GLOW} !important;
            outline: none !important;
          }
          .signup-input-password:hover { border-color: rgba(255,255,255,0.12) !important; }
          @media (max-width: 480px) {
            .signup-name-fields { flex-direction: column !important; }
          }
        `}
      </style>

      {/* Car icon watermark - top center of card */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        opacity: 0.06,
        pointerEvents: 'none',
      }}>
        <svg width="48" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 17h14v-5H5v5z" />
          <path d="M5 12l2-4h10l2 4" />
          <circle cx="7.5" cy="17" r="1.5" />
          <circle cx="16.5" cy="17" r="1.5" />
        </svg>
      </div>

      {/* Card header: Create Your Account + subhead; Sign in link top-right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Title level={2} style={{
            color: THEME_COLORS.DARK.text.primary,
            marginBottom: '4px',
            marginTop: 0,
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '1.2',
          }}>
            Create Your Account
          </Title>
          <Text style={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: '14px',
            display: 'block',
            lineHeight: '1.5',
          }}>
            Set up your Hooptie login in 30 seconds.
          </Text>
        </div>
        <Text style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', flexShrink: 0 }}>
          Already have an account? <Link to="/sign-in" style={{ color: COLORS.PRIMARY[400], fontWeight: 500 }}>Sign in</Link>
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ maxWidth: '100%' }}
      >
        {/* Error Message */}
        {error && (
          <div style={{
            marginBottom: '24px',
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${COLORS.ERROR[500]}`,
            borderRadius: '8px',
            color: COLORS.ERROR[500],
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {error}
          </div>
        )}

        {/* Name Fields - Two Columns; stack on mobile */}
        <div className="signup-name-fields" style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          <Form.Item
            name="firstName"
            rules={[{ required: true, message: 'Please enter your first name' }]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <div>
              <Text style={{ 
                color: THEME_COLORS.DARK.text.primary, 
                fontWeight: 500, 
                fontSize: '14px',
                display: 'block',
                marginBottom: '8px'
              }}>
                First Name <span style={{ color: COLORS.ERROR[500] }}>*</span>
              </Text>
              <Input
                placeholder="John"
                className="signup-input"
                style={{
                  background: INPUT_BG,
                  border: `1px solid ${INPUT_BORDER}`,
                  borderRadius: '12px',
                  color: THEME_COLORS.DARK.text.primary,
                  height: '50px',
                  padding: '0 16px',
                  fontSize: '14px',
                }}
              />
            </div>
          </Form.Item>

          <Form.Item
            name="lastName"
            rules={[{ required: true, message: 'Please enter your last name' }]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <div>
              <Text style={{ 
                color: THEME_COLORS.DARK.text.primary, 
                fontWeight: 500, 
                fontSize: '14px',
                display: 'block',
                marginBottom: '8px'
              }}>
                Last Name <span style={{ color: COLORS.ERROR[500] }}>*</span>
              </Text>
              <Input
                placeholder="Doe"
                className="signup-input"
                style={{
                  background: INPUT_BG,
                  border: `1px solid ${INPUT_BORDER}`,
                  borderRadius: '12px',
                  color: THEME_COLORS.DARK.text.primary,
                  height: '50px',
                  padding: '0 16px',
                  fontSize: '14px',
                }}
              />
            </div>
          </Form.Item>
        </div>

        {/* Email Field */}
        <Form.Item
          name="email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
          style={{ marginBottom: '24px' }}
        >
          <div>
            <Text style={{ 
              color: THEME_COLORS.DARK.text.primary, 
              fontWeight: 500, 
              fontSize: '14px',
              display: 'block',
              marginBottom: '8px'
            }}>
              Email <span style={{ color: COLORS.ERROR[500] }}>*</span>
            </Text>
            <Input
              placeholder="john.doe@example.com"
              className="signup-input"
              style={{
                background: INPUT_BG,
                border: `1px solid ${INPUT_BORDER}`,
                borderRadius: '12px',
                color: THEME_COLORS.DARK.text.primary,
                height: '50px',
                padding: '0 16px',
                fontSize: '14px',
              }}
            />
          </div>
        </Form.Item>

        {/* Phone Number Field - saved to Clerk user unsafeMetadata */}
        <Form.Item
          name="phone"
          style={{ marginBottom: '24px' }}
        >
          <div>
            <Text style={{ 
              color: THEME_COLORS.DARK.text.primary, 
              fontWeight: 500, 
              fontSize: '14px',
              display: 'block',
              marginBottom: '8px'
            }}>
              Phone number
            </Text>
            <Input
              placeholder="(555) 123-4567"
              className="signup-input"
              style={{
                background: INPUT_BG,
                border: `1px solid ${INPUT_BORDER}`,
                borderRadius: '12px',
                color: THEME_COLORS.DARK.text.primary,
                height: '50px',
                padding: '0 16px',
                fontSize: '14px',
              }}
            />
          </div>
        </Form.Item>

        {/* Password Field */}
        <Form.Item
          name="password"
          rules={[
            { required: true, message: 'Please enter a password' },
            { min: 8, message: 'Password must be at least 8 characters' },
            { pattern: /\d/, message: 'Password must contain at least one number' },
          ]}
          style={{ marginBottom: '8px' }}
        >
          <div>
            <Text style={{ color: THEME_COLORS.DARK.text.primary, fontWeight: 500, fontSize: '14px', display: 'block', marginBottom: '8px' }}>
              Password <span style={{ color: COLORS.ERROR[500] }}>*</span>
            </Text>
            <Input.Password
              placeholder="Enter your password"
              className="signup-input-password"
              style={{
                background: INPUT_BG,
                border: `1px solid ${INPUT_BORDER}`,
                borderRadius: '12px',
                color: THEME_COLORS.DARK.text.primary,
                height: '50px',
                padding: '0 16px',
                fontSize: '14px',
              }}
              iconRender={(visible) => (
                <span style={{ color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                  {visible ? '👁️' : '👁️‍🗨️'}
                </span>
              )}
            />
          </div>
        </Form.Item>
        <div style={{ marginBottom: '24px', fontSize: '12px', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {passwordValid ? <span style={{ color: '#22c55e' }}>✓</span> : <span style={{ opacity: 0.7 }}>○</span>}
          <span>8+ characters / 1 number</span>
        </div>

        {/* Confirm Password Field */}
        <Form.Item
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Please confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
          style={{ marginBottom: '40px' }}
        >
          <div>
            <Text style={{ 
              color: THEME_COLORS.DARK.text.primary, 
              fontWeight: 500, 
              fontSize: '14px',
              display: 'block',
              marginBottom: '8px'
            }}>
              Confirm Password <span style={{ color: COLORS.ERROR[500] }}>*</span>
            </Text>
            <Input.Password
              placeholder="Confirm your password"
              className="signup-input-password"
              style={{
                background: INPUT_BG,
                border: `1px solid ${INPUT_BORDER}`,
                borderRadius: '12px',
                color: THEME_COLORS.DARK.text.primary,
                height: '50px',
                padding: '0 16px',
                fontSize: '14px',
              }}
              iconRender={(visible) => (
                <span style={{ color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                  {visible ? '👁️' : '👁️‍🗨️'}
                </span>
              )}
            />
          </div>
        </Form.Item>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: '24px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!isLoaded}
            size="large"
            style={{
              background: GRADIENTS.PRIMARY,
              border: 'none',
              height: '46px',
              padding: '0 28px',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: 600,
              boxShadow: '0 4px 20px rgba(59, 130, 246, 0.35)',
            }}
          >
            Continue
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default SignUpStep;

